import {
  AfterViewInit, Component, OnInit,
  Input, ViewChild,
  ElementRef, EventEmitter,
  Output, TemplateRef, forwardRef
} from '@angular/core';
import { MatAutocomplete, FloatLabelType } from '@angular/material';
import { AutocompleteService } from './autocomplete.service';
import { HttpParams } from '@angular/common/http';
import { NG_VALUE_ACCESSOR, ControlValueAccessor, FormControl, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Subject } from 'rxjs';
import "rxjs/add/operator/debounceTime";
import "rxjs/add/operator/distinctUntilChanged";
// import { map } from 'rxjs/operators';
// import { debug } from 'util';
// import { Observable } from "rxjs/Observable";
// import "rxjs/add/operator/mergeMap";

@Component({
  selector: 'ng-mat-autocomplete',
  templateUrl: './autocomplete.component.html',
  styleUrls: ['./autocomplete.component.css'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => AutocompleteComponent),
    multi: true
  }]
})
export class AutocompleteComponent implements AfterViewInit, OnInit, ControlValueAccessor {

  @Input() set source(value: AutocompleteService | any[]) {
    if (this.isAutocompleteService(value)) {
      this.service = value;
    } else
      if (value instanceof Array) {
        this.storedItems = value.slice(0);
        this.saveReturnType(this.storedItems);
      }
  }

  @Input() name = '';
  @Input() placeholder = '';
  @Input() Id = '';
  @Input() validation = false;
  @Input() floatLabel: FloatLabelType = 'auto';
  @Input() formControl?: FormControl;
  @Input() doPrefetch = false;
  @Input() displayItem?= 'item.name';
  @Input() hasSearchButton = false;
  @Input() hasProgressBar = false;
  @Input() minChars = 2;
  @Input() clearAfterSearch = false;
  @Input() showAddNew = false;
  @Input() addNewText = 'Add new';
  @Input() isFocused = false;
  @Input() validationError: string;
  @Input() serviceParams?: HttpParams;
  @Input() displayItemFn?: (item: any) => string;
  @Input() displayTemplate?: TemplateRef<any>;
  @Input() transformResult: any = (x: any[]) => x;
  @Input() label = null;

  @Output() modelChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() optionSelected = new EventEmitter();
  @Output() createNew = new EventEmitter();

  @ViewChild('autocompleteInput', { static: false }) autocompleteInput: ElementRef;
  @ViewChild('autocomplete', { static: false }) autocomplete: MatAutocomplete;

  public selectedOption: any;
  public query = '';
  public autocompleteList: any[] | null;
  public request = false;
  public noSuggestions: boolean;
  public requestsInQueue = 0;

  private storedItems?: any[];
  private service?: AutocompleteService;
  private returnType: string;
  private subscription: Subscription;
  private searchTextChanged = new Subject<string>();

  constructor() { }

  ngOnInit() {
    if (this.validation) {
      this.formControl = new FormControl('', [Validators.required]);
    }
    if (this.doPrefetch) {
      this.prefetch();
    }
  }

  getErrorMessage() {
    return this.formControl.hasError('required') ? this.validationError : '';
  }

  setValue() {
    return this.formControl.setValue('');
  }

  ngAfterViewInit() {
    if (this.isFocused) {
      setTimeout(() => {
        this.autocompleteInput.nativeElement.focus();
      });
    }
  }

  public prefetch() {
    if (!this.service) {
      throw new Error('Service for prefetch is not defined in \'Source\'');
    }

    this.storedItems = [];
    this.noSuggestions = false;

    let params = new HttpParams();
    if (this.serviceParams) {
      params = this.serviceParams;
    }

    this.service.fetch(params).then((result: any) => {
      this.storedItems = this.transformResult(result);
      this.noSuggestions = result.length === 0;
      this.saveReturnType(this.storedItems);
    });
  }

  public search() {
    if (this.doSearchViaService) {
      this.fetch(true);
    } else {
      this.filterStoredItems(true);
    }
  }

  public fetch(force?: boolean) {
    if (!this.service) {
      throw new Error('Service for fetch is not defined in \'Source\'');
    }

    this.query = this.autocompleteInput.nativeElement.value;

    // empty query is not allowed for autocomplete
    if (this.isQueryEmpty(this.query) && this.minChars !== 0) {
      this.autocompleteList = [];
      this.noSuggestions = false;
      return;
    }

    if (force || this.query.length >= this.minChars) {
      let params = new HttpParams();
      params = params.set('filter', this.query);

      if (this.Id != '') {
        params = params.set('parentId', this.Id);
      }

      if (this.serviceParams) {
        this.serviceParams.set('filter', this.query);
        if (this.Id != '') {
          this.serviceParams.set('parentId', this.Id);
        }
        params = this.serviceParams;
      }

      this.requestsInQueue = this.requestsInQueue + 1;

      this.service.fetch(params)
        .then((result: any) => {
          this.requestsInQueue = this.requestsInQueue - 1;
          this.autocompleteList = this.transformResult(result);
          this.noSuggestions = this.autocompleteList.length === 0;
          this.saveReturnType(this.autocompleteList);
        });
    }
  }

  public filterStoredItems(force?: boolean) {
    if (!this.displayItem && !this.displayItemFn) {
      throw new Error('You must provide displayItem or displayItemFn for local search.');
    }

    this.query = this.autocompleteInput.nativeElement.value;

    // empty query is not allowed for autocomplete
    if (this.isQueryEmpty(this.query) && this.minChars !== 0) {
      this.autocompleteList = [];
      return;
    }

    if (force || this.query.length >= this.minChars) {
      this.noSuggestions = false;

      if (!this.storedItems) {
        return;
      }

      this.autocompleteList = this.storedItems.filter(item => {
        if (!this.viewItem(item)) {
          throw new Error('String to evaluate in displayItem was provided wrong. Better use displayItemFn');
        }

        let formatedItem = this.viewItem(item).toLowerCase();
        if (this.displayItemFn) {
          formatedItem = this.displayItemFn(item).toLowerCase();
        }
        return formatedItem.indexOf(this.query.toLowerCase()) > -1;
      });
      this.noSuggestions = this.autocompleteList.length === 0;
    }
  }

  public autocompleteSelected($event: any) {
    this.query = this.autocompleteInput.nativeElement.value;
    const selected = $event.option.value;
    this.writeValue(selected);

    if (selected) {
      this.optionSelected.emit(selected);
    }

    if (this.clearAfterSearch) {
      this.clearValue();
    }
  }

  public autocompleteDisplayFn() {
    if (this.displayItemFn) {
      return this.displayItemFn;
    }

    return (item: any) => {
      return item ? this.viewItem(item) : item;
    };
  }

  public onKey($event: KeyboardEvent) {
    this.searchTextChanged.next(this.autocompleteInput.nativeElement.value);
    this.subscription = this.searchTextChanged
    .debounceTime(1300)
    .distinctUntilChanged()
    .subscribe((search) => { 
      if ($event.keyCode < 37 || $event.keyCode > 40) {
        if (this.autocompleteInput.nativeElement.value === '') {
          this.clearValue();
        }
        this.onKeyCallback();
      }
    });

    // prevent filtering results if arrow were pressed
    // if ($event.keyCode < 37 || $event.keyCode > 40) {
    //   if (this.autocompleteInput.nativeElement.value === '') {
    //     this.clearValue();
    //   }
    //   this.onKeyCallback();
    // }
  }

  public onKeyCallback() {
    if (this.doSearchViaService) {
      this.fetch();
    } else {
      this.filterStoredItems();
    }
  }

  public onBlur($event: MouseEvent) {
    this.query = this.autocompleteInput.nativeElement.value;
    if (this.selectedOption) {
      if (this.returnType === typeof this.selectedOption
        && this.viewItem(this.selectedOption) !== this.query) {
        this.autocompleteInput.nativeElement.value = this.viewItem(this.selectedOption);
      }
    } else {
      this.clearValue();
    }
  }

  public onFocus($event: any) {
    if (this.selectedOption) {
      return;
    }

    if (this.doSearchViaService) {
      this.fetch();
    } else {
      this.filterStoredItems();
    }
  }

  public viewItem(item: any) {
    if (this.displayItemFn) {
      return this.displayItemFn(item);
    }
    // using eval() can be dangerous, better use displayItemFn function
    return this.displayItem ? eval(this.displayItem) : item.name;
  }

  public resetForm() {
    this.formControl.clearValidators();
    this.formControl.setErrors(null);
    this.formControl.reset();
    this.formControl.setValidators([Validators.required]);
  }

  public clearValue() {
    if (this.formControl) {
      this.formControl.reset();
    }
    this.selectedOption = null;
    this.autocompleteInput.nativeElement.value = '';
    this.query = '';
    this.onChange(this.selectedOption);
  }

  get doSearchViaService() {
    // check if search result returns from service or from local data
    // if prefetch is active only one request will be made on init
    return this.service && !this.doPrefetch;
  }

  public onCreateNew() {
    if (this.selectedOption) {
      const value = this.returnType === typeof this.selectedOption ? this.viewItem(this.selectedOption) : this.selectedOption;
      this.autocompleteInput.nativeElement.value = value;
    }

    this.createNew.emit(this.selectedOption);
  }

  private isQueryEmpty(query: string): boolean {
    return query.length <= 0;
  }

  private isAutocompleteService(object: any): object is AutocompleteService {
    return object && 'fetch' in object;
  }

  private saveReturnType(items: any[] | undefined | null) {
    if (items && items.length > 0) {
      this.returnType = typeof items[0];
    }
  }

  writeValue(val: any): void {
    if (!val) {
      return;
    }
    this.selectedOption = val;

    if (this.autocompleteInput) {
      this.autocompleteInput.nativeElement.value = this.viewItem(this.selectedOption);
    }

    this.onChange(val);
  }

  onChange: any = () => { };
  onTouched: any = () => { };

  registerOnChange(fn: any): void { this.onChange = fn; }
  registerOnTouched(fn: any): void { this.onTouched = fn; }

  setDisabledState?(isDisabled: boolean): void {
    throw new Error('Method not implemented.');
  }
}
